/**
 * SizilPro — CoverLetterEditor
 * Step 3: reusable company intro / cover letter foundation.
 *
 * Updated to support:
 * - clearer AI vs manual paths
 * - stronger connection to the live preview
 * - cleaner handoff into templates and final proposals
 */

import { useState, useEffect } from "react";
import type { ReactNode } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Loader2,
  Sparkles,
  Pencil,
  RefreshCw,
  FileText,
  ArrowRight,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  useBrandSettings,
  useUpsertBrandSettings,
} from "../../hooks/useBrandSettings";
import { useGenerateCoverLetter } from "../../hooks/useAIWriter";
import type { CoverLetterAnswers } from "../../types";
import {
  SetupFieldGroup,
  SetupLabel,
  SetupInput,
  SetupTextarea,
  SetupSelect,
  SetupActions,
} from "./SetupPrimitives";

type Mode = "choose" | "ai-form" | "ai-result" | "manual";

const aiSchema = z.object({
  company: z.string().min(2, "Required"),
  differentiator: z.string().min(10, "Tell us more (10+ characters)"),
  quality: z.string().min(10, "Tell us more (10+ characters)"),
  idealClient: z.string().min(10, "Tell us more (10+ characters)"),
  tone: z.enum([
    "Professional and formal",
    "Warm and personal",
    "Confident and direct",
  ]),
});

interface Props {
  onComplete: () => void;
}

type CoverLetterGenerateResult = {
  cover_letter_html?: string;
  coverLetter?: string;
  signoff?: string | null;
  suggested_tagline?: string | null;
};

function normalizeGeneratedCoverLetter(
  result: CoverLetterGenerateResult
): {
  html: string;
  signoff: string;
  tagline: string;
} {
  return {
    html: result.cover_letter_html ?? result.coverLetter ?? "",
    signoff: result.signoff ?? "",
    tagline: result.suggested_tagline ?? "",
  };
}

function QuestionCard({
  number,
  title,
  description,
  children,
}: {
  number: number;
  title: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <section className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 md:p-6">
      <div className="mb-5 flex items-start gap-4">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white text-sm font-semibold text-black">
          {number}
        </div>
        <div className="min-w-0">
          <h3 className="text-base font-semibold text-white">{title}</h3>
          {description ? (
            <p className="mt-1 text-sm leading-6 text-white/45">{description}</p>
          ) : null}
        </div>
      </div>
      {children}
    </section>
  );
}

function ModeCard({
  icon,
  title,
  description,
  recommended,
  onClick,
}: {
  icon: ReactNode;
  title: string;
  description: string;
  recommended?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "group relative flex flex-col gap-3 rounded-xl border p-5 text-left transition-all",
        "border-white/10 hover:border-white/25 hover:bg-white/5"
      )}
    >
      {recommended && (
        <span className="absolute right-3 top-3 rounded-full bg-emerald-500/20 px-2 py-0.5 text-[10px] font-medium text-emerald-400">
          Recommended
        </span>
      )}

      <span className="text-white/60 transition-colors group-hover:text-white">
        {icon}
      </span>

      <div>
        <p className="text-sm font-semibold">{title}</p>
        <p className="mt-1 text-xs leading-relaxed text-white/40">
          {description}
        </p>
      </div>
    </button>
  );
}

export function CoverLetterEditor({ onComplete }: Props) {
  const { data: brand } = useBrandSettings();
  const upsert = useUpsertBrandSettings();
  const generate = useGenerateCoverLetter();

  const [mode, setMode] = useState<Mode>("choose");
  const [generatedHtml, setGeneratedHtml] = useState("");
  const [generatedSignoff, setGeneratedSignoff] = useState("");
  const [generatedTagline, setGeneratedTagline] = useState("");
  const [manualHtml, setManualHtml] = useState("");

  useEffect(() => {
    const existingHtml = brand?.cover_letter_html ?? "";
    const hasSavedCoverLetter = existingHtml.trim().length > 0;

    setGeneratedHtml(existingHtml);
    setGeneratedSignoff(brand?.signoff ?? "");
    setGeneratedTagline(brand?.suggested_tagline ?? "");
    setManualHtml(existingHtml);
    setMode(hasSavedCoverLetter ? "ai-result" : "choose");
  }, [brand?.cover_letter_html, brand?.signoff, brand?.suggested_tagline]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
    reset,
  } = useForm<CoverLetterAnswers>({
    resolver: zodResolver(aiSchema),
    defaultValues: {
      company: "",
      differentiator: "",
      quality: "",
      idealClient: "",
      tone: "Warm and personal",
    },
  });

  useEffect(() => {
    reset({
      company: brand?.company_name ?? "",
      differentiator: "",
      quality: "",
      idealClient: "",
      tone: "Warm and personal",
    });
  }, [brand?.company_name, reset]);

  const onGenerate = async (answers: CoverLetterAnswers) => {
    const rawResult = (await generate.mutateAsync(
      answers
    )) as CoverLetterGenerateResult;

    const result = normalizeGeneratedCoverLetter(rawResult);

    setGeneratedHtml(result.html);
    setGeneratedSignoff(result.signoff);
    setGeneratedTagline(result.tagline);
    setMode("ai-result");
  };

  const onRegenerate = async () => {
    const answers = getValues();
    const rawResult = (await generate.mutateAsync(
      answers
    )) as CoverLetterGenerateResult;

    const result = normalizeGeneratedCoverLetter(rawResult);

    setGeneratedHtml(result.html);
    setGeneratedSignoff(result.signoff);
    setGeneratedTagline(result.tagline);
  };

  const handleSave = async () => {
    if (mode === "manual") {
      await upsert.mutateAsync({
        cover_letter_html: manualHtml,
      });
    } else {
      await upsert.mutateAsync({
        cover_letter_html: generatedHtml,
        signoff: generatedSignoff || null,
        suggested_tagline: generatedTagline || null,
      });
    }

    onComplete();
  };

  const handleSkip = () => {
    onComplete();
  };

  if (mode === "choose") {
    return (
      <div className="space-y-6">
        <div className="rounded-2xl border border-amber-500/15 bg-amber-500/5 p-4">
          <div className="flex items-start gap-3">
            <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" />
            <div>
              <p className="text-sm font-medium text-amber-300">
                This becomes your reusable company introduction
              </p>
              <p className="mt-1 text-sm leading-6 text-white/45">
                The cover letter is the foundation intro that can appear in your
                templates and proposals. The live preview on the right will begin
                reflecting this step as soon as you save it.
              </p>
            </div>
          </div>
        </div>

        <QuestionCard
          number={1}
          title="How do you want to create your cover letter?"
          description="You can let AI draft a polished first pass from your answers, or write your own manually."
        >
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <ModeCard
              icon={<Sparkles className="h-5 w-5" />}
              title="Generate with AI"
              description="Answer a few guided questions and generate a reusable intro matched to your company."
              recommended
              onClick={() => setMode("ai-form")}
            />

            <ModeCard
              icon={<Pencil className="h-5 w-5" />}
              title="Write manually"
              description="Paste or write your own reusable company intro with full control."
              onClick={() => setMode("manual")}
            />
          </div>
        </QuestionCard>

        <QuestionCard
          number={2}
          title="Where this gets used"
          description="This is not just for one proposal."
        >
          <div className="grid gap-3 md:grid-cols-3">
            <div className="rounded-xl border border-white/10 bg-black/20 p-4">
              <FileText className="h-4 w-4 text-white/35" />
              <p className="mt-3 text-sm font-medium text-white/80">Live preview</p>
              <p className="mt-1 text-xs leading-6 text-white/40">
                The setup preview starts showing your company voice here.
              </p>
            </div>

            <div className="rounded-xl border border-white/10 bg-black/20 p-4">
              <FileText className="h-4 w-4 text-white/35" />
              <p className="mt-3 text-sm font-medium text-white/80">Templates</p>
              <p className="mt-1 text-xs leading-6 text-white/40">
                This becomes the reusable intro foundation templates build on.
              </p>
            </div>

            <div className="rounded-xl border border-white/10 bg-black/20 p-4">
              <ArrowRight className="h-4 w-4 text-white/35" />
              <p className="mt-3 text-sm font-medium text-white/80">Proposals</p>
              <p className="mt-1 text-xs leading-6 text-white/40">
                Final proposals inherit this tone and intro direction later.
              </p>
            </div>
          </div>
        </QuestionCard>

        <button
          type="button"
          onClick={handleSkip}
          className="text-sm text-white/30 underline underline-offset-2 transition-colors hover:text-white/50"
        >
          Skip for now
        </button>
      </div>
    );
  }

  if (mode === "ai-form") {
    return (
      <form onSubmit={handleSubmit(onGenerate)} className="space-y-6">
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
          <p className="text-sm leading-6 text-white/50">
            Answer these questions in your own words. The AI will use your answers
            to create a reusable company intro, not just a generic template.
          </p>
        </div>

        <QuestionCard
          number={1}
          title="What is your company name and what services do you specialize in?"
        >
          <SetupFieldGroup>
            <SetupInput
              id="company"
              placeholder="Hatch Cleaning Co. — commercial janitorial and restaurant cleaning"
              error={errors.company?.message}
              {...register("company")}
            />
          </SetupFieldGroup>
        </QuestionCard>

        <QuestionCard
          number={2}
          title="What makes your company different from other service companies?"
        >
          <SetupFieldGroup>
            <SetupTextarea
              rows={3}
              placeholder="We specialize exclusively in restaurant cleaning and have trained staff for health code compliance..."
              error={errors.differentiator?.message}
              {...register("differentiator")}
            />
          </SetupFieldGroup>
        </QuestionCard>

        <QuestionCard
          number={3}
          title="How would you describe your commitment to quality?"
        >
          <SetupFieldGroup>
            <SetupTextarea
              rows={3}
              placeholder="Every clean is inspected before we leave. Our clients know exactly what to expect every time..."
              error={errors.quality?.message}
              {...register("quality")}
            />
          </SetupFieldGroup>
        </QuestionCard>

        <QuestionCard
          number={4}
          title="Who are your ideal clients and what do you help them achieve?"
        >
          <SetupFieldGroup>
            <SetupTextarea
              rows={3}
              placeholder="Restaurant owners and managers who need reliable, health-code-compliant cleaning without micromanaging..."
              error={errors.idealClient?.message}
              {...register("idealClient")}
            />
          </SetupFieldGroup>
        </QuestionCard>

        <QuestionCard
          number={5}
          title="What tone should the cover letter use?"
        >
          <SetupFieldGroup>
            <SetupSelect {...register("tone")}>
              <option value="Warm and personal">Warm and personal</option>
              <option value="Professional and formal">Professional and formal</option>
              <option value="Confident and direct">Confident and direct</option>
            </SetupSelect>
          </SetupFieldGroup>
        </QuestionCard>

        <SetupActions>
          <button
            type="submit"
            disabled={generate.isPending}
            className="flex items-center gap-2 rounded-lg bg-white px-6 py-2.5 text-sm font-semibold text-black transition-colors hover:bg-white/90 disabled:opacity-50"
          >
            {generate.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4" />
                Generate cover letter
              </>
            )}
          </button>

          <button
            type="button"
            onClick={() => setMode("choose")}
            className="text-sm text-white/40 transition-colors hover:text-white/60"
          >
            Back
          </button>
        </SetupActions>
      </form>
    );
  }

  if (mode === "ai-result") {
    return (
      <div className="space-y-6">
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
          <div className="flex items-center justify-between gap-4">
            <p className="flex items-center gap-2 text-sm text-white/50">
              <Sparkles className="h-4 w-4 text-emerald-400" />
              AI-generated reusable cover letter
            </p>

            <button
              type="button"
              onClick={() => void onRegenerate()}
              disabled={generate.isPending}
              className="flex items-center gap-1.5 text-xs text-white/40 transition-colors hover:text-white/70"
            >
              {generate.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <RefreshCw className="h-3.5 w-3.5" />
              )}
              Regenerate
            </button>
          </div>

          <p className="mt-2 text-xs leading-5 text-white/35">
            This will act as your reusable intro foundation for templates and final proposals.
          </p>
        </div>

        <div
          className="prose prose-sm max-w-none rounded-xl bg-white p-6 text-sm leading-relaxed text-gray-800"
          dangerouslySetInnerHTML={{ __html: generatedHtml }}
        />

        <div className="rounded-xl border border-white/10 bg-black/20 p-4">
          <p className="text-xs uppercase tracking-[0.14em] text-white/30">
            Preview connection
          </p>
          <p className="mt-2 text-sm leading-6 text-white/45">
            Once saved, this intro becomes the basis for the cover-letter area you
            see in Brand Setup preview and the reusable company voice that flows forward.
          </p>
        </div>

        <SetupActions>
          <button
            type="button"
            onClick={() => void handleSave()}
            disabled={upsert.isPending}
            className="flex items-center gap-2 rounded-lg bg-white px-6 py-2.5 text-sm font-semibold text-black transition-colors hover:bg-white/90 disabled:opacity-50"
          >
            {upsert.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Use this & continue
          </button>

          <button
            type="button"
            onClick={() => {
              setManualHtml(generatedHtml);
              setMode("manual");
            }}
            className="text-sm text-white/40 transition-colors hover:text-white/60"
          >
            Edit manually instead
          </button>
        </SetupActions>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
        <p className="text-sm leading-6 text-white/50">
          Write or paste the reusable company intro you want associated with your
          brand. This should be broad enough to reuse across templates and proposals.
        </p>
      </div>

      <QuestionCard
        number={1}
        title="Write your reusable cover letter"
        description="Write your cover letter below. You can use paragraph breaks to separate sections."
      >
        <div>
          <SetupLabel>Cover letter</SetupLabel>
          <SetupTextarea
            rows={14}
            value={manualHtml}
            onChange={(e) => setManualHtml(e.target.value)}
            placeholder="Dear [Client],&#10;&#10;Thank you for the opportunity to present this proposal..."
            className="font-mono text-xs"
          />
        </div>
      </QuestionCard>

      {manualHtml && (
        <QuestionCard
          number={2}
          title="Preview"
          description="This is how the saved content will render in your reusable cover-letter foundation."
        >
          <div
            className="prose prose-sm max-w-none rounded-xl bg-white p-6 text-sm leading-relaxed text-gray-800"
            dangerouslySetInnerHTML={{ __html: manualHtml }}
          />
        </QuestionCard>
      )}

      <SetupActions>
        <button
          type="button"
          onClick={() => void handleSave()}
          disabled={upsert.isPending || !manualHtml.trim()}
          className="flex items-center gap-2 rounded-lg bg-white px-6 py-2.5 text-sm font-semibold text-black transition-colors hover:bg-white/90 disabled:opacity-50"
        >
          {upsert.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
          Save & continue
        </button>

        <button
          type="button"
          onClick={() => setMode("choose")}
          className="text-sm text-white/40 transition-colors hover:text-white/60"
        >
          Back
        </button>
      </SetupActions>
    </div>
  );
}